import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

export interface DriverMission {
  id: string;
  order_id: string;
  accepted_at: string | null;
  declined_at: string | null;
  completed_at: string | null;
  booked_at: string | null;
  assigned_at: string | null;
  started_at: string | null;
  delay_reported_at: string | null;
  estimated_delay_minutes: number | null;
  delay_reason: string | null;
  orders: {
    order_number: string;
    cargo_title: string;
    cargo_description: string;
    pickup_address: string;
    pickup_contact: string;
    pickup_phone: string;
    pickup_date: string;
    delivery_address: string;
    delivery_contact: string;
    delivery_phone: string;
    delivery_date: string;
  };
}

export const useDriverMissions = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const { data: missions = [], isLoading } = useQuery({
    queryKey: ['driver-missions', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];

      // Get driver_id from drivers table
      const { data: driverData, error: driverError } = await supabase
        .from('drivers')
        .select('id')
        .eq('user_id', user.id)
        .maybeSingle();

      if (driverError) throw driverError;
      if (!driverData) return [];

      const { data, error } = await supabase
        .from('transport_jobs')
        .select(`
          id,
          order_id,
          accepted_at,
          declined_at,
          completed_at,
          booked_at,
          assigned_at,
          started_at,
          delay_reported_at,
          estimated_delay_minutes,
          delay_reason,
          orders (
            order_number,
            cargo_title,
            cargo_description,
            pickup_address,
            pickup_contact,
            pickup_phone,
            pickup_date,
            delivery_address,
            delivery_contact,
            delivery_phone,
            delivery_date
          )
        `)
        .eq('driver_id', driverData.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as DriverMission[];
    },
    enabled: !!user?.id,
  });

  const acceptMission = useMutation({
    mutationFn: async (jobId: string) => {
      const { error } = await supabase
        .from('transport_jobs')
        .update({ accepted_at: new Date().toISOString() })
        .eq('id', jobId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['driver-missions'] });
      toast.success('Mission acceptée');
    },
    onError: () => {
      toast.error('Erreur lors de l\'acceptation');
    },
  });

  const declineMission = useMutation({
    mutationFn: async (jobId: string) => {
      const { error } = await supabase
        .from('transport_jobs')
        .update({ 
          declined_at: new Date().toISOString(),
          driver_id: null 
        })
        .eq('id', jobId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['driver-missions'] });
      toast.success('Mission refusée');
    },
    onError: () => {
      toast.error('Erreur lors du refus');
    },
  });

  const assignedMissions = missions.filter(
    m => !m.accepted_at && !m.declined_at && !m.completed_at
  );
  const activeMissions = missions.filter(
    m => m.accepted_at && !m.completed_at
  );
  const completedMissions = missions.filter(m => m.completed_at);

  return {
    missions,
    assignedMissions,
    activeMissions,
    completedMissions,
    isLoading,
    acceptMission: acceptMission.mutateAsync,
    declineMission: declineMission.mutateAsync,
    isAccepting: acceptMission.isPending,
    isDeclining: declineMission.isPending,
  };
};
